import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
const MAX_PHOTOS = 7;
const MAX_FILE_SIZE = 6.9 * 1024 * 1024; // 6.9MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const PhotoUpload = ({ showToast, onUpdate }) => {
  const { token, user, setUser } = useAuth();
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  
  // Resolve image URL - handles both absolute URLs and API-relative paths
  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API.replace('/api', '')}${url}`;
    return url;
  };
  
  const profilePhoto = user?.profile_image_url;
  const additionalPhotos = user?.additional_image_urls || [];
  const totalPhotos = (profilePhoto ? 1 : 0) + additionalPhotos.length;
  
  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please use JPEG, PNG, or WebP';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 6.9MB';
    }
    return null;
  };
  
  const handleFileSelect = async (e, isProfile = false) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check total count
    const remainingSlots = MAX_PHOTOS - totalPhotos;
    if (files.length > remainingSlots && !isProfile) {
      showToast && showToast(`You can only upload ${remainingSlots} more photo(s)`, 'error');
      return;
    }
    
    setUploading(true);
    
    try {
      for (let i = 0; i < (isProfile ? 1 : files.length); i++) {
        const file = files[i];
        const validationError = validateFile(file);
        if (validationError) {
          showToast && showToast(validationError, 'error');
          continue;
        }
        
        setUploadProgress(prev => ({ ...prev, [file.name]: 'uploading' }));
        
        // Upload file directly to backend
        const formData = new FormData();
        formData.append('file', file);
        
        const slot = isProfile ? 'profile' : 'additional';
        try {
          const data = await post(`/users/upload-photo-file?slot=${slot}`, { body: formData });
          setUser && setUser(data.user);
          onUpdate && onUpdate(data.user);
          setUploadProgress(prev => ({ ...prev, [file.name]: 'done' }));
        } catch (uploadErr) {
          showToast && showToast(typeof uploadErr === 'string' ? uploadErr : 'Upload failed', 'error');
          setUploadProgress(prev => ({ ...prev, [file.name]: 'error' }));
        }
      }
      
      showToast && showToast('Photos updated successfully!', 'success');
    } catch (err) {
      showToast && showToast('Upload failed: ' + err.message, 'error');
    }
    
    setUploading(false);
    setUploadProgress({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  const handleDeletePhoto = async (index) => {
    try {
      const data = await xhrDel(`/users/photos/${index}`);
      setUser && setUser(data.user);
      onUpdate && onUpdate(data.user);
      showToast && showToast(data.message, 'success');
    } catch (err) {
      showToast && showToast(typeof err === 'string' ? err : 'Delete failed', 'error');
    }
  };
  
  return (
    <div data-testid="photo-upload-container" style={{
      background: 'rgba(30, 30, 35, 0.9)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 16,
      padding: 24
    }}>
      <h3 style={{ 
        margin: '0 0 8px 0', 
        color: '#0F172A',
        fontSize: '1.1rem'
      }}>
        Profile Photos
      </h3>
      <p style={{ 
        color: '#a1a1aa', 
        fontSize: '0.85rem', 
        marginBottom: 20,
        lineHeight: 1.5
      }}>
        Optional: Upload photos for your profile. Recommended: 1-2 headshots and 2 full-body shots. 
        Maximum {MAX_PHOTOS} photos total.
      </p>
      
      {/* Photo count indicator */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        padding: '8px 12px',
        background: 'rgba(245, 158, 11, 0.1)',
        borderRadius: 8
      }}>
        <span style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>
          Photos uploaded
        </span>
        <span style={{ 
          color: totalPhotos >= MAX_PHOTOS ? '#ef4444' : '#3b82f6', 
          fontWeight: 600 
        }}>
          {totalPhotos} / {MAX_PHOTOS}
        </span>
      </div>
      
      {/* Profile Photo Section */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ 
          display: 'block',
          color: '#d1d5db', 
          fontSize: '0.9rem', 
          marginBottom: 10,
          fontWeight: 500
        }}>
          Profile Photo (Headshot)
        </label>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Profile photo preview */}
          <div style={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            border: '3px solid rgba(245, 158, 11, 0.5)',
            overflow: 'hidden',
            background: 'rgba(30, 30, 35, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {profilePhoto ? (
              <img 
                src={resolveImageUrl(profilePhoto)} 
                alt="Profile" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                data-testid="profile-photo"
              />
            ) : (
              <span style={{ color: '#6b7280', fontSize: 40 }}>👤</span>
            )}
          </div>
          
          {/* Upload/delete buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #3b82f6, #FCD34D)',
              color: '#0F172A',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500,
              textAlign: 'center'
            }}>
              {profilePhoto ? 'Change' : 'Upload'} Profile Photo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFileSelect(e, true)}
                style={{ display: 'none' }}
                disabled={uploading}
                data-testid="profile-photo-input"
              />
            </label>
            
            {profilePhoto && (
              <button
                onClick={() => handleDeletePhoto(-1)}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
                data-testid="delete-profile-photo"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Additional Photos Section */}
      <div>
        <label style={{ 
          display: 'block',
          color: '#d1d5db', 
          fontSize: '0.9rem', 
          marginBottom: 10,
          fontWeight: 500
        }}>
          Additional Photos ({additionalPhotos.length}/6)
        </label>
        
        {/* Photos grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: 12,
          marginBottom: 16
        }}>
          {additionalPhotos.map((url, idx) => (
            <div 
              key={idx}
              style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: 8,
                overflow: 'hidden',
                border: '2px solid rgba(255,255,255,0.1)'
              }}
            >
              <img 
                src={resolveImageUrl(url)} 
                alt={`Photo ${idx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                data-testid={`additional-photo-${idx}`}
              />
              <button
                onClick={() => handleDeletePhoto(idx)}
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.9)',
                  color: '#0F172A',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                data-testid={`delete-photo-${idx}`}
              >
                ✕
              </button>
            </div>
          ))}
          
          {/* Add more photos button */}
          {additionalPhotos.length < 6 && (
            <label style={{
              aspectRatio: '1',
              borderRadius: 8,
              border: '2px dashed rgba(255,255,255,0.2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: totalPhotos >= MAX_PHOTOS ? 'not-allowed' : 'pointer',
              opacity: totalPhotos >= MAX_PHOTOS ? 0.5 : 1,
              transition: 'all 0.2s'
            }}>
              <span style={{ fontSize: 24, color: '#6b7280' }}>+</span>
              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>Add</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={(e) => handleFileSelect(e, false)}
                style={{ display: 'none' }}
                disabled={uploading || totalPhotos >= MAX_PHOTOS}
                data-testid="additional-photos-input"
              />
            </label>
          )}
        </div>
        
        {/* Guidelines */}
        <div style={{
          padding: 12,
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: 8,
          fontSize: '0.8rem',
          color: '#a1a1aa',
          lineHeight: 1.6
        }}>
          <strong style={{ color: '#d1d5db' }}>Photo guidelines (recommended):</strong>
          <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
            <li>1-2 frontal or partly-side upper torso/headshots</li>
            <li>2 high-quality almost-full or full-body shots</li>
            <li>Accepted formats: JPEG, PNG, WebP (max 6.9MB each)</li>
          </ul>
        </div>
      </div>
      
      {/* Upload progress */}
      {uploading && (
        <div style={{
          marginTop: 16,
          padding: 12,
          background: 'rgba(245, 158, 11, 0.15)',
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <div style={{ 
            width: 24, 
            height: 24, 
            border: '3px solid rgba(245, 158, 11, 0.3)',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 8px'
          }} />
          <span style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>
            Uploading photos...
          </span>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PhotoUpload;
